import { createStore, type StoreApi } from 'zustand/vanilla';
import type { Geometry } from '../doc/schema';
import type { Rect, Size } from '../doc/geometry';
import type { Camera } from './viewport/camera';

export type Tool =
  | 'select'
  | 'pan'
  | 'note'
  | 'title'
  | 'richtext'
  | 'table'
  | 'todo'
  | 'kanban'
  | 'shape'
  | 'frame'
  | 'draw'
  | 'cutout'
  | 'sheet'
  | 'exportArea';

/** How a stroke looks while the eraser is still working on it (see eraser.ts). */
export interface InkPreview {
  /** A line: the pieces left so far, each flat [x, y, pressure, ...] in the stroke's own space, with its cut flags. */
  runs?: { points: number[]; cut: number }[];
  /** A fill: its erased paths so far (in place of the stored ones). */
  erased?: number[][];
}

/**
 * Per-pane UI state. Everything that is *not* document content: camera, selection, tool,
 * in-progress drag geometry. A second pane (split view) simply gets a second store.
 */
export interface PaneState {
  camera: Camera;
  /** Zoom after the user stopped zooming; image LOD switches on this, not on every frame. */
  settledZoom: number;
  size: Size;
  tool: Tool;
  selection: ReadonlySet<string>;
  editingId: string | null;
  /** Geometry overrides while dragging/resizing, committed to the doc on release. */
  transient: Readonly<Record<string, Partial<Geometry>>>;
  /** Strokes the eraser is working on, drawn from here until it lets go. */
  inkPreview: Readonly<Record<string, InkPreview>>;
  marquee: Rect | null;
  /** Rectangle being dragged out with a creation tool (shape). */
  creating: Rect | null;
  /** Snap guide lines (world coords) shown during a drag. */
  snapGuides: { x: number | null; y: number | null };
  spaceHeld: boolean;
  dragOver: boolean;
  /** Sketch sheet being painted (the sheet tool in paint mode), and its active layer. */
  paintSheetId: string | null;
  paintLayerId: string | null;

  setCamera(c: Camera): void;
  setSize(s: Size): void;
  setTool(t: Tool): void;
  select(ids: Iterable<string>): void;
  addToSelection(ids: Iterable<string>): void;
  toggleSelected(id: string): void;
  clearSelection(): void;
  setEditing(id: string | null): void;
  setTransient(t: Record<string, Partial<Geometry>>): void;
  clearTransient(): void;
  setInkPreview(p: Record<string, InkPreview> | null): void;
  setMarquee(r: Rect | null): void;
  setCreating(r: Rect | null): void;
  setSnapGuides(g: { x: number | null; y: number | null }): void;
  setSpaceHeld(v: boolean): void;
  setDragOver(v: boolean): void;
  /** Enters paint mode on a sheet (tool becomes 'sheet'); null leaves it (tool stays 'sheet' for placing). */
  setPaint(sheetId: string | null, layerId?: string | null): void;
  setPaintLayer(layerId: string | null): void;
}

export type PaneStore = StoreApi<PaneState>;

const EMPTY_SELECTION: ReadonlySet<string> = new Set();
const EMPTY_TRANSIENT: Readonly<Record<string, Partial<Geometry>>> = Object.freeze({});
const NO_PREVIEW: Readonly<Record<string, InkPreview>> = Object.freeze({});
const NO_GUIDES = Object.freeze({ x: null, y: null }) as { x: number | null; y: number | null };

export function createPaneStore(initialCamera: Camera = { x: 0, y: 0, zoom: 1, rot: 0 }): PaneStore {
  let settleTimer: ReturnType<typeof setTimeout> | undefined;

  return createStore<PaneState>((set, get) => ({
    camera: initialCamera,
    settledZoom: initialCamera.zoom,
    size: { w: 0, h: 0 },
    tool: 'select',
    selection: EMPTY_SELECTION,
    editingId: null,
    transient: EMPTY_TRANSIENT,
    inkPreview: NO_PREVIEW,
    marquee: null,
    creating: null,
    snapGuides: NO_GUIDES,
    spaceHeld: false,
    dragOver: false,
    paintSheetId: null,
    paintLayerId: null,

    setCamera(camera) {
      const prev = get().camera;
      set({ camera });
      if (camera.zoom !== prev.zoom) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => set({ settledZoom: get().camera.zoom }), 150);
      }
    },
    setSize(size) {
      const s = get().size;
      if (s.w !== size.w || s.h !== size.h) set({ size });
    },
    setTool(tool) {
      if (get().tool === tool) return;
      // any other tool ends paint mode
      set(tool === 'sheet' ? { tool, creating: null } : { tool, creating: null, paintSheetId: null, paintLayerId: null });
    },
    select(ids) {
      const next = new Set(ids);
      const cur = get().selection;
      if (next.size === cur.size && [...next].every((id) => cur.has(id))) return;
      set({ selection: next.size ? next : EMPTY_SELECTION });
    },
    addToSelection(ids) {
      const next = new Set(get().selection);
      for (const id of ids) next.add(id);
      set({ selection: next });
    },
    toggleSelected(id) {
      const next = new Set(get().selection);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      set({ selection: next.size ? next : EMPTY_SELECTION });
    },
    clearSelection() {
      if (get().selection.size) set({ selection: EMPTY_SELECTION });
    },
    setEditing(editingId) {
      if (get().editingId !== editingId) set({ editingId });
    },
    setTransient(transient) {
      set({ transient });
    },
    clearTransient() {
      if (get().transient !== EMPTY_TRANSIENT) set({ transient: EMPTY_TRANSIENT });
    },
    setInkPreview(p) {
      const inkPreview = p ?? NO_PREVIEW;
      if (get().inkPreview !== inkPreview) set({ inkPreview });
    },
    setMarquee(marquee) {
      set({ marquee });
    },
    setCreating(creating) {
      set({ creating });
    },
    setSnapGuides(g) {
      const cur = get().snapGuides;
      if (cur.x === g.x && cur.y === g.y) return;
      set({ snapGuides: g.x === null && g.y === null ? NO_GUIDES : g });
    },
    setSpaceHeld(spaceHeld) {
      if (get().spaceHeld !== spaceHeld) set({ spaceHeld });
    },
    setDragOver(dragOver) {
      if (get().dragOver !== dragOver) set({ dragOver });
    },
    setPaint(paintSheetId, layerId) {
      const s = get();
      const paintLayerId = paintSheetId ? (layerId === undefined ? (paintSheetId === s.paintSheetId ? s.paintLayerId : null) : layerId) : null;
      if (s.paintSheetId === paintSheetId && s.paintLayerId === paintLayerId && (s.tool === 'sheet' || !paintSheetId)) return;
      set(paintSheetId ? { tool: 'sheet', creating: null, paintSheetId, paintLayerId } : { paintSheetId: null, paintLayerId: null });
    },
    setPaintLayer(paintLayerId) {
      if (get().paintLayerId !== paintLayerId) set({ paintLayerId });
    },
  }));
}
